// The Inbox tab's rules, kept out of the components so they can be read, and tested,
// without a renderer. Every rule here mirrors cvce `protex/api/decisions.py::_validate`
// so the Record button can say WHY it is disabled; the server stays the authority.

import manifest from "./manifest.json";
import {
  decideRequest,
  ProtexError,
  type DecideIn,
  type DecisionRequest,
  type Fact,
  type Scope,
} from "./api";
import { daysSince, formulaLabel, marketLabel, productLabel } from "./format";

/** One server page. The module never holds a collection (AGENTS: portal is not a backend). */
export const PAGE_SIZE = 50;

export const COVERAGE_FACT_KEY = "policy.support_coverage";

/** The list query key, shared so the page and its tests read the same cache entry. */
export const requestsKey = (status: StatusFilter, page: number) =>
  ["protex", "requests", "list", status, page] as const;

/** The server's own topic rule, so a topic that passes here cannot 422 as `topic_required`. */
export const TOPIC_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const TOPIC_MAX = 64;
export const RATIONALE_MAX = 2000;
export const CONDITION_MAX = 200;

// ---------------------------------------------------------------- the URL

export type StatusFilter = "waiting" | "decided" | "all";

export function statusFilterFrom(raw: unknown): StatusFilter {
  return raw === "decided" || raw === "all" ? raw : "waiting";
}

export function apiStatusFor(filter: StatusFilter): "open" | "decided" | "all" {
  return filter === "waiting" ? "open" : filter;
}

/** 1-based page from the URL; anything unreadable is page 1. */
export function pageFrom(raw: unknown): number {
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 ? n : 1;
}

export function pageCount(total: number): number {
  return Math.max(1, Math.ceil(total / PAGE_SIZE));
}

// ------------------------------------------------------------ people, dates

/** "celeste@ketosource.co" -> "Celeste". Names are read off the address, never invented. */
export function nameFromEmail(email: string | null | undefined): string {
  const local = (email ?? "").split("@")[0].split(/[._-]/)[0];
  return local ? local.charAt(0).toUpperCase() + local.slice(1) : "Someone";
}

function plural(days: number): string {
  return days === 1 ? "1 day" : `${days} days`;
}

/** "waiting 3 days" / "today". The oldest wait is the most urgent, so age shows on every row. */
export function waitingLabel(createdAt: string, now: Date = new Date()): string {
  const days = daysSince(createdAt, now);
  return days <= 0 ? "today" : `waiting ${plural(days)}`;
}

export function isDeferred(request: Pick<DecisionRequest, "status" | "decision">): boolean {
  return request.status === "open" && request.decision?.deferred === true;
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** "deferred 2 days ago", from `decision.decided_at` (SOFT 16). */
export function deferredAgo(request: DecisionRequest, now: Date = new Date()): string | null {
  if (!isDeferred(request)) return null;
  const at = text(request.decision?.decided_at);
  if (!at) return "deferred";
  const days = daysSince(at, now);
  return days <= 0 ? "deferred today" : `deferred ${plural(days)} ago`;
}

/** "9 Oct". UTC, like every other date in this module, so two readers see one day. */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
  });
}

export function scopePhrase(scope: Scope | null | undefined): string {
  if (!scope) return "every market";
  const label = marketLabel(scope);
  return label === "All markets" ? "every market" : label;
}

function scopeOf(decision: Record<string, unknown> | null): Scope | null {
  const raw = decision?.scope;
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Record<string, unknown>;
  return {
    region: text(s.region) || "*",
    store: text(s.store) || "*",
    channel: text(s.channel) || "*",
    locale: text(s.locale) || "*",
  };
}

/** The outcome as a sentence, never an enum. */
export function outcomeSentence(decision: Record<string, unknown> | null): string {
  if (!decision) return "";
  const where = scopePhrase(scopeOf(decision));
  const outcome = text(decision.outcome);
  if (text(decision.decision_type) === "usage_ramp") {
    return outcome === "defer"
      ? "we are not ready to publish the dosing ramp yet"
      : `we publish the standard dosing ramp in ${where}`;
  }
  const topic = text(decision.topic) || "this topic";
  if (outcome === "defer") return `we are not ready to decide ${topic} yet`;
  return outcome === "do_not_cover"
    ? `we do not support ${topic} in ${where}`
    : `we support ${topic} in ${where}`;
}

/** "Decided 12 Oct by Damien: we support reactive-hypoglycaemia in every market." */
export function decidedSentence(request: DecisionRequest): string {
  const when = request.decided_at ? ` ${shortDate(request.decided_at)}` : "";
  return `Decided${when} by ${nameFromEmail(request.decided_by)}: ${outcomeSentence(request.decision)}.`;
}

export function rationaleOf(request: Pick<DecisionRequest, "decision">): string {
  return text(request.decision?.rationale);
}

// ------------------------------------------------------- facts the form reads

/** `region` is the market the answer was decided for, so `*` can say which one it shows. */
export type TopicOption = {
  topic: string;
  covered: boolean;
  decidedAt: string | null;
  region: string;
};

/**
 * Existing topics in one market (any market for `*`), from the approved
 * `policy.support_coverage` fact.
 * The fact holds `value.topics`, a map of topic -> entry; its keys are the picker.
 * For `*`, an every-market answer wins; otherwise the first market by name, and its
 * `region` says which market the answer belongs to.
 */
export function topicsFrom(facts: readonly Fact[], region: string): TopicOption[] {
  const options = new Map<string, TopicOption>();
  for (const fact of facts) {
    if (fact.fact_key !== COVERAGE_FACT_KEY || fact.status !== "approved") continue;
    // `*` is "every market": the form's default, and the wrong place to hide what is
    // already decided in one. A specific market still reads only its own topics.
    if (region !== "*" && fact.scope.region !== region) continue;
    const topics = fact.value?.topics;
    if (!topics || typeof topics !== "object") continue;
    for (const [topic, entry] of Object.entries(topics as Record<string, unknown>)) {
      const e = entry && typeof entry === "object" ? (entry as Record<string, unknown>) : {};
      const held = options.get(topic);
      if (held && !outranks(fact.scope.region, held.region)) continue;
      options.set(topic, {
        topic,
        covered: e.covered === true,
        decidedAt: typeof e.decided_at === "string" ? e.decided_at : null,
        region: fact.scope.region,
      });
    }
  }
  return [...options.values()].sort((a, b) => a.topic.localeCompare(b.topic));
}

/** Which market's answer a `*` picker shows: every market first, then by name. Stable across reads. */
function outranks(region: string, held: string): boolean {
  // Two facts for one market: the later one wins, as it always has.
  if (region === held) return true;
  if (held === "*") return false;
  if (region === "*") return true;
  return region.localeCompare(held) < 0;
}

export type FormulaOption = { id: string; label: string };

/** Formula choices from facts the module already reads. No new endpoint. */
export function formulasFrom(facts: readonly Fact[]): FormulaOption[] {
  const options = new Map<string, FormulaOption>();
  for (const fact of facts) {
    if (!fact.formula_version_id || !fact.product_name) continue;
    // A formula only counts if something about it is approved now: a superseded
    // formula is no longer sold, and a ramp published against it is a ramp nobody uses.
    if (fact.status !== "approved") continue;
    if (options.has(fact.formula_version_id)) continue;
    options.set(fact.formula_version_id, {
      id: fact.formula_version_id,
      label: productLabel(fact) || (formulaLabel(fact.formula_version_label) ?? fact.product_name),
    });
  }
  return [...options.values()].sort((a, b) => a.label.localeCompare(b.label));
}

export type MarketOption = { region: string; label: string };

/** Markets seen on existing facts (the same limit as v1 C-5: a market with no fact yet cannot be named). */
export function marketsFrom(facts: readonly Fact[]): MarketOption[] {
  const regions = new Set<string>();
  for (const fact of facts) if (fact.scope.region !== "*") regions.add(fact.scope.region);
  return [...regions]
    .sort((a, b) => a.localeCompare(b))
    .map((region) => ({
      region,
      label: marketLabel({ region, store: "*", channel: "*", locale: "*" }),
    }));
}

// ------------------------------------------------------------- the form

export type Kind = "coverage" | "ramp";
export type Outcome = "cover" | "do_not_cover" | "defer";

export type FormState = {
  kind: Kind;
  /** Whatever was typed or picked. `slugify` turns it into the topic that is sent. */
  topic: string;
  outcome: Outcome;
  rationale: string;
  /** `*` is every market. */
  region: string;
  formulaId: string;
  start: string;
  target: string;
  step: string;
  interval: string;
  condition: string;
  cortexQuestionId: string;
};

export function emptyForm(request: Pick<DecisionRequest, "cortex_question_id">): FormState {
  return {
    kind: "coverage",
    topic: "",
    outcome: "cover",
    rationale: "",
    region: "*",
    formulaId: "",
    start: "",
    target: "",
    step: "",
    interval: "",
    condition: "",
    cortexQuestionId: request.cortex_question_id ?? "",
  };
}

/**
 * Switching the kind resets what belonged to the other kind, and keeps what the
 * person typed that applies to both: the reason, the market, the linked question.
 * A reason that vanished because a radio was pressed is the worst kind of lost work.
 */
export function changeKind(state: FormState, kind: Kind): FormState {
  if (kind === state.kind) return state;
  return {
    ...emptyForm({ cortex_question_id: state.cortexQuestionId || null }),
    kind,
    rationale: state.rationale,
    region: state.region,
  };
}

/** Free text into a topic slug, live: "Reactive Hypoglycaemia!" -> "reactive-hypoglycaemia". */
export function slugify(raw: string): string {
  return raw
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export type FieldKey =
  | "topic"
  | "formula"
  | "outcome"
  | "start"
  | "target"
  | "step"
  | "interval"
  | "condition"
  | "ramp"
  | "rationale";

export type Problems = Partial<Record<FieldKey, string>>;

export const MESSAGES = {
  topic: "Topics are lowercase words joined by hyphens, like reactive-hypoglycaemia.",
  formula: "That product formula is not in Protex any more. Pick another.",
} as const;

/**
 * A plain decimal, as typed: "5", "0.5", "12.25". Not "1e2", "0x1F", " 5" or "5.".
 * A minus sign parses, so "-1" is told "must be more than zero" rather than "enter a number".
 */
const PLAIN_DECIMAL = /^-?\d+(\.\d+)?$/;

/** What a ramp field that is not a plain decimal says, with the shape it wants. */
export const NOT_A_NUMBER = "Enter a plain number, like 5 or 0.5, with no spaces.";

/** The number in one ramp field, or `null`. Exported for its tests. */
export function amount(raw: string): number | null {
  if (!PLAIN_DECIMAL.test(raw)) return null;
  const n = Number(raw);
  // A digit string long enough to overflow is Infinity, which JSON sends as null.
  return Number.isFinite(n) ? n : null;
}

/**
 * What stops the button, and in which words.
 *
 * `reason` is the FIRST thing missing, shown beside the disabled button. `problems`
 * are values that were entered and are wrong, shown under their own field. Empty
 * required fields are a reason only, so a fresh form does not open covered in errors.
 */
export function validate(state: FormState): { reason: string | null; problems: Problems } {
  const problems: Problems = {};
  let reason: string | null = null;
  const note = (message: string) => {
    reason ??= message;
  };

  if (state.kind === "coverage") {
    const topic = slugify(state.topic);
    if (!topic) note("Pick or type a topic.");
    else if (topic.length > TOPIC_MAX || !TOPIC_PATTERN.test(topic)) {
      problems.topic = MESSAGES.topic;
      note(MESSAGES.topic);
    }
  } else {
    if (!state.formulaId) note("Pick the product formula.");
  }

  if (state.kind === "ramp" && state.outcome === "do_not_cover") {
    problems.outcome = "A ramp can be published or deferred, not declined.";
    note(problems.outcome);
  }

  if (state.kind === "ramp" && state.outcome === "cover") {
    const fields: Array<[FieldKey, string]> = [
      ["start", state.start],
      ["target", state.target],
      ["step", state.step],
      ["interval", state.interval],
    ];
    const values = fields.map(([, raw]) => amount(raw));
    if (values.some((v) => v === null)) {
      note("Fill in start, target, step and days between steps.");
      fields.forEach(([key, raw]) => {
        if (raw.trim() !== "" && amount(raw) === null) problems[key] = NOT_A_NUMBER;
      });
    } else {
      const [start, target] = values as number[];
      fields.forEach(([key], i) => {
        if ((values[i] as number) <= 0) problems[key] = "Must be more than zero.";
      });
      if (Object.keys(problems).some((k) => ["start", "target", "step", "interval"].includes(k))) {
        note("Start, target, step and days between steps must each be more than zero.");
      } else if (start > target) {
        problems.start = "Start cannot be higher than the target.";
        note(problems.start);
      }
    }
    const condition = state.condition.trim();
    if (condition === "") note("Say when the ramp applies, in 200 characters or fewer.");
    else if (condition.length > CONDITION_MAX) {
      problems.condition = "Keep the condition to 200 characters or fewer.";
      note(problems.condition);
    }
  }

  const rationale = state.rationale.trim();
  if (rationale === "") note("Say why, in a sentence.");
  else if (rationale.length > RATIONALE_MAX) {
    problems.rationale = "Keep the reason to 2000 characters or fewer.";
    note(problems.rationale);
  }

  return { reason, problems };
}

/** The exact `DecideIn` the server is sent. `null` while the form is not valid. */
export function buildBody(state: FormState): DecideIn | null {
  if (validate(state).reason !== null) return null;
  const scope: Scope = { region: state.region || "*", store: "*", channel: "*", locale: "*" };
  const linked = state.cortexQuestionId.trim();
  const common = {
    outcome: state.outcome,
    rationale: state.rationale.trim(),
    scope,
    ...(linked ? { cortex_question_id: linked } : {}),
  };
  if (state.kind === "coverage") {
    return { decision_type: "support_coverage", ...common, topic: slugify(state.topic) };
  }
  if (state.outcome === "defer") {
    return { decision_type: "usage_ramp", ...common, formula_version_id: state.formulaId };
  }
  return {
    decision_type: "usage_ramp",
    ...common,
    formula_version_id: state.formulaId,
    ramp: {
      start_ml: Number(state.start),
      target_ml: Number(state.target),
      step_ml: Number(state.step),
      step_interval_days: Number(state.interval),
      condition: state.condition.trim(),
    },
  };
}

/** Defer publishes nothing, so it needs no confirm (UX Flow A). */
export function needsConfirm(state: FormState): boolean {
  return state.outcome !== "defer";
}

// -------------------------------------------------------------- failures

/** Who to ask, read from the manifest's owner so no address is typed twice. */
const OWNER = manifest.owner_email;

/** A refused DECISION: the person could read the request but may not decide it. */
export const REFUSED = `Only the Protex owner can decide requests. Ask ${OWNER}.`;
/**
 * A refused LIST: the person may not open the Inbox at all. Not about deciding (bug 3f80402f).
 * Any Protex viewer may read the Inbox (GATE-476), so the copy names no level.
 */
export const LIST_REFUSED = `You don't have access to the Inbox. Ask ${OWNER}.`;
/** What a viewer reads where the decision form would be (bug 3f80402f-8134). Owner from the manifest. */
export const WAITING_FOR_OWNER = `Waiting for ${nameFromEmail(OWNER)}'s decision.`;
export const UNCONFIRMED = "We could not confirm your access, so nothing is shown.";
export const ALREADY_DECIDED = "This request was already decided.";
export const GONE = "This request no longer exists.";
export const NOT_ACCEPTED = "That decision was not accepted. Check the form and try again.";

/**
 * Where a 409 is shown. On its own row when that row is still open (or nothing is),
 * otherwise as a notice above the list: the owner has moved to another row, and the
 * decided row has left Waiting, so without the notice the refusal is silent.
 */
export function conflictLanding(selectedId: string | null, id: string): "pane" | "notice" {
  return selectedId === null || selectedId === id ? "pane" : "notice";
}

/** The notice for a 409 that came back after the owner moved to another row. */
export function conflictElsewhereMessage(question: string | null | undefined): string {
  const which = question?.trim() ? `"${question.trim()}"` : "a request";
  return `Your decision on ${which} was not saved: it was already decided. Until you leave this page, your reason is kept on that request, under Decided.`;
}

/** The portal's own words for a 403 (`{detail: {reason}}`), so the owner can see which grant decided it. */
function refusalReason(body: unknown): string {
  const detail = detailOf(body);
  return detail && typeof detail === "object" && !Array.isArray(detail)
    ? text((detail as Record<string, unknown>).reason)
    : "";
}

/** The list failed. Never an empty list: 403 names who to ask, anything else says nothing is shown. */
export function listFailureMessage(error: unknown): string {
  if (!(error instanceof ProtexError) || error.status !== 403) return UNCONFIRMED;
  const reason = refusalReason(error.body).replace(/[.\s]+$/, "");
  return reason ? `${LIST_REFUSED} Reason: ${reason}.` : LIST_REFUSED;
}

/** Each 422 `reason` from `_validate`, mapped to the field it is about. */
const REASONS: Record<string, [FieldKey, string]> = {
  topic_required: ["topic", MESSAGES.topic],
  unknown_formula_version: ["formula", MESSAGES.formula],
  ramp_fields_required: ["ramp", "Fill in start, target, step and days between steps."],
  ramp_values_must_be_positive: [
    "ramp",
    "Start, target, step and days between steps must each be more than zero.",
  ],
  ramp_start_exceeds_target: ["start", "Start cannot be higher than the target."],
  ramp_condition_required: ["condition", "Say when the ramp applies, in 200 characters or fewer."],
  invalid_outcome_for_ramp: ["outcome", "A ramp can be published or deferred, not declined."],
};

export type DecideFailure =
  | { kind: "conflict" }
  | { kind: "gone" }
  | { kind: "forbidden" }
  | { kind: "fields"; problems: Problems }
  | { kind: "unreachable" }
  | { kind: "other" };

function detailOf(body: unknown): unknown {
  return body && typeof body === "object" ? (body as Record<string, unknown>).detail : undefined;
}

export function classifyDecideError(error: unknown): DecideFailure {
  if (!(error instanceof ProtexError)) return { kind: "unreachable" };
  switch (error.status) {
    case 403:
      return { kind: "forbidden" };
    case 404:
      return { kind: "gone" };
    case 409:
      return { kind: "conflict" };
    case 503:
      return { kind: "unreachable" };
    case 422: {
      const detail = detailOf(error.body);
      const reason =
        detail && typeof detail === "object" && !Array.isArray(detail)
          ? text((detail as Record<string, unknown>).reason)
          : "";
      const mapped = REASONS[reason];
      if (mapped) return { kind: "fields", problems: { [mapped[0]]: mapped[1] } };
      // A request-shape error (FastAPI validation): name the field when it names one.
      if (Array.isArray(detail)) {
        const loc = detail
          .map((d) => (d && typeof d === "object" ? (d as { loc?: unknown[] }).loc : undefined))
          .find(Array.isArray);
        if (loc?.includes("rationale")) {
          return { kind: "fields", problems: { rationale: "Keep the reason to 2000 characters or fewer." } };
        }
      }
      return { kind: "other" };
    }
    default:
      return { kind: "other" };
  }
}

/** The alert text for a failure that is not about one field. */
export function failureMessage(failure: DecideFailure): string {
  switch (failure.kind) {
    case "forbidden":
      return REFUSED;
    case "gone":
      return GONE;
    case "conflict":
      return ALREADY_DECIDED;
    case "unreachable":
      return UNCONFIRMED;
    case "fields":
    case "other":
      return NOT_ACCEPTED;
  }
}

export type DecideResult =
  | { ok: true; request: DecisionRequest }
  | { ok: false; failure: DecideFailure };

/** Send one decision and sort the answer into something the pane can render. */
export async function submitDecision(requestId: string, body: DecideIn): Promise<DecideResult> {
  try {
    const response = await decideRequest(requestId, body);
    return { ok: true, request: response.data };
  } catch (error) {
    return { ok: false, failure: classifyDecideError(error) };
  }
}

/**
 * Which request the pane is about. The person's choice if it is still in the list,
 * otherwise the first row, so the Inbox always opens on something to decide.
 */
export function paneRequest(
  items: readonly DecisionRequest[],
  selectedId: string | null,
): DecisionRequest | null {
  return items.find((r) => r.request_id === selectedId) ?? items[0] ?? null;
}
